import {
  ArrowUpRight,
  CaretDown,
  Check,
  EnvelopeSimple,
  GithubLogo,
  ShieldCheck
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import {
  assuranceWorkflow,
  companyThesis,
  currentAsks,
  defensibility,
  founders,
  monthlyUpdates,
  proofPoints,
  type MetricKey,
  type MonthlyUpdate
} from "./data/updates";
import { track } from "./lib/analytics";

const formatCompact = (value: number) =>
  new Intl.NumberFormat("en-US", {
    notation: value >= 10000 ? "compact" : "standard",
    maximumFractionDigits: 1
  }).format(value);

const formatFull = (value: number, maximumFractionDigits = 0) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits,
    minimumFractionDigits: maximumFractionDigits
  }).format(value);

const metricLabels: Record<MetricKey, string> = {
  visitors: "Unique visitors",
  signIns: "People who signed in",
  signInEvents: "Sign-in events",
  newSignups: "New signups",
  totalSignups: "Total signups",
  cliInstalls: "CLI installs",
  prReviews: "PR reviews",
  mrr: "Month-end MRR",
  activeUsers: "Monthly active users",
  reviewsScanned: "Reviews scanned",
  scanRuns: "Scan runs",
  installRuns: "Install-script runs",
  apiRequests: "API requests",
  errorRate: "Error rate",
  revenue: "Booked revenue",
  payingUsers: "Paying customers",
  digitalSignups: "Digital signups",
  oohSignups: "Offline signups",
  pageviews: "Pageviews",
  blogViews: "Blog views"
};

const quickLinks = [
  { id: "company", label: "60-second overview" },
  { id: "traction", label: "Traction" },
  { id: "product", label: "Product" },
  { id: "defensibility", label: "Defensibility" },
  { id: "team", label: "Team" },
  { id: "monthly-report", label: "Monthly updates" },
  { id: "current-asks", label: "Current asks" }
];

function MetricValue({
  value,
  prefix = "",
  suffix = "",
  compact = true,
  maximumFractionDigits = 0
}: {
  value: number | null | undefined;
  prefix?: string;
  suffix?: string;
  compact?: boolean;
  maximumFractionDigits?: number;
}) {
  if (value === null || value === undefined) {
    return <span className="not-reported">Not reported</span>;
  }

  return (
    <>
      {prefix}
      {compact ? formatCompact(value) : formatFull(value, maximumFractionDigits)}
      {suffix}
    </>
  );
}

function DataTooltip({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
    dataKey?: string;
  }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div className="chart-tooltip">
      <span>{label}</span>
      {payload
        .filter((item) => item.value !== null && item.value !== undefined)
        .map((item) => (
          <strong key={item.dataKey}>
            {item.name}: {formatCompact(item.value ?? 0)}
            {item.dataKey === "errorRate" ? "%" : ""}
          </strong>
        ))}
    </div>
  );
}

function OverviewChart() {
  const data = monthlyUpdates.map((update) => ({
    month: update.shortMonth,
    visitors: update.metrics.visitors,
    signIns: update.metrics.signInEvents
  }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 12, right: 0, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--line)" strokeDasharray="2 5" />
        <XAxis
          dataKey="month"
          tickLine={false}
          axisLine={false}
          tick={{ fill: "var(--muted)", fontSize: 12 }}
        />
        <YAxis
          yAxisId="visitors"
          tickLine={false}
          axisLine={false}
          tick={{ fill: "var(--muted)", fontSize: 11 }}
        />
        <YAxis
          yAxisId="signIns"
          orientation="right"
          tickLine={false}
          axisLine={false}
          tick={{ fill: "var(--accent)", fontSize: 11 }}
          domain={[0, "dataMax + 15"]}
        />
        <Tooltip content={<DataTooltip />} cursor={{ fill: "var(--hover)" }} />
        <Bar
          name="Visitors"
          dataKey="visitors"
          yAxisId="visitors"
          fill="var(--ink)"
          radius={[3, 3, 0, 0]}
          maxBarSize={34}
        />
        <Line
          name="Sign-in events"
          type="monotone"
          dataKey="signIns"
          yAxisId="signIns"
          stroke="var(--accent)"
          strokeWidth={3}
          connectNulls
          isAnimationActive={false}
          dot={{ fill: "var(--accent)", strokeWidth: 0, r: 4 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function ApiChart() {
  const data = monthlyUpdates.map((update) => ({
    month: update.shortMonth,
    apiRequests: update.metrics.apiRequests
  }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="apiFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.34} />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="var(--line)" strokeDasharray="2 5" />
        <XAxis
          dataKey="month"
          tickLine={false}
          axisLine={false}
          tick={{ fill: "var(--muted)", fontSize: 12 }}
        />
        <YAxis
          tickFormatter={(value) => formatCompact(value)}
          tickLine={false}
          axisLine={false}
          tick={{ fill: "var(--muted)", fontSize: 11 }}
        />
        <Tooltip content={<DataTooltip />} />
        <Area
          name="API requests"
          dataKey="apiRequests"
          type="monotone"
          stroke="var(--accent)"
          strokeWidth={3}
          fill="url(#apiFill)"
          connectNulls
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function ReliabilityChart() {
  const latestMonth = monthlyUpdates.at(-1)!.shortMonth;
  const data = monthlyUpdates.map((update) => ({
    month: update.shortMonth,
    errorRate: update.metrics.errorRate
  }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 12, right: 8, left: 2, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--line)" strokeDasharray="2 5" />
        <XAxis
          dataKey="month"
          tickLine={false}
          axisLine={false}
          tick={{ fill: "var(--muted)", fontSize: 12 }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fill: "var(--muted)", fontSize: 11 }}
          unit="%"
        />
        <Tooltip content={<DataTooltip />} cursor={{ fill: "var(--hover)" }} />
        <Bar
          name="Error rate"
          dataKey="errorRate"
          radius={[3, 3, 0, 0]}
          maxBarSize={42}
          isAnimationActive={false}
        >
          {data.map((entry) => (
            <Cell
              key={entry.month}
              fill={entry.month === latestMonth ? "var(--accent)" : "var(--ink)"}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function MonthRail({
  selected,
  onSelect
}: {
  selected: string;
  onSelect: (update: MonthlyUpdate) => void;
}) {
  return (
    <div className="month-rail" aria-label="Monthly updates">
      {monthlyUpdates.map((update) => (
        <button
          className={selected === update.id ? "month-button active" : "month-button"}
          key={update.id}
          onClick={() => onSelect(update)}
          type="button"
        >
          <span>{update.shortMonth}</span>
          <small>2026</small>
        </button>
      ))}
    </div>
  );
}

function DetailList({
  title,
  items,
  tone
}: {
  title: string;
  items: string[];
  tone?: "accent" | "quiet";
}) {
  return (
    <section className={`detail-list ${tone ?? ""}`}>
      <h3>{title}</h3>
      <ol>
        {items.map((item) => (
          <li key={item}>
            <span className="list-marker">
              <Check size={13} weight="bold" aria-hidden="true" />
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function App() {
  const [selectedId, setSelectedId] = useState(monthlyUpdates.at(-1)!.id);
  const [menuOpen, setMenuOpen] = useState(false);

  const selected = useMemo(
    () => monthlyUpdates.find((update) => update.id === selectedId)!,
    [selectedId]
  );

  const selectMonth = (update: MonthlyUpdate, source = "monthly_rail") => {
    setSelectedId(update.id);
    track("investor update selected", {
      month: update.month,
      update_id: update.id,
      source
    });
    requestAnimationFrame(() => {
      document.getElementById("monthly-report")?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start"
      });
    });
  };

  const latest = monthlyUpdates.at(-1)!;
  const previous = monthlyUpdates.at(-2)!;
  const latestMonthName = latest.month.replace(/\s\d{4}$/, "");
  const growthNote = (key: MetricKey) => {
    const current = latest.metrics[key];
    const prior = previous.metrics[key];
    if (current == null || prior == null || prior === 0) return "Comparison not reported";
    const change = ((current - prior) / prior) * 100;
    return `${change >= 0 ? "+" : ""}${change.toFixed(1)}% month over month`;
  };

  const trackShortcut = (section: string, location: string) => {
    track("investor shortcut clicked", { section, location });
    setMenuOpen(false);
  };

  const trackOutbound = (destination: string, location: string) => {
    track("investor outbound link clicked", { destination, location });
  };

  useEffect(() => {
    track("investor page viewed", {
      latest_month: latest.month,
      available_updates: monthlyUpdates.length
    });

    const engagedTimer = window.setTimeout(() => {
      track("investor engaged reading reached", { seconds: 30 });
    }, 30_000);

    const reachedDepths = new Set<number>();
    const onScroll = () => {
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;

      const depth = Math.round((window.scrollY / scrollableHeight) * 100);
      [25, 50, 75, 90].forEach((milestone) => {
        if (depth >= milestone && !reachedDepths.has(milestone)) {
          reachedDepths.add(milestone);
          track("investor reading depth reached", { percent: milestone });
        }
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(engagedTimer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [latest.month]);

  useEffect(() => {
    const viewedSections = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const section = entry.target.getAttribute("data-track-section");
          if (!entry.isIntersecting || !section || viewedSections.has(section)) return;

          viewedSections.add(section);
          track("investor section viewed", { section });
        });
      },
      { threshold: 0.15 }
    );

    document.querySelectorAll("[data-track-section]").forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="site-shell">
      <a className="skip-link" href="#top">
        Skip to content
      </a>
      <header className="topbar">
        <a className="brand" href="#top" aria-label="Autter investor updates home">
          <img src="/assets/autter-logo-light.webp" alt="Autter" />
          <small>Investor updates</small>
        </a>

        <nav className={menuOpen ? "nav-links open" : "nav-links"}>
          <a href="#company" onClick={() => trackShortcut("company", "header")}>
            Overview
          </a>
          <a
            href="#monthly-report"
            onClick={() => trackShortcut("monthly-report", "header")}
          >
            Latest update
          </a>
          <a
            className="nav-cta"
            href="mailto:sagnik@autter.dev"
            onClick={() => track("investor contact clicked", { location: "header" })}
          >
            Contact
          </a>
        </nav>

        <button
          className="menu-button"
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
          onClick={() => {
            setMenuOpen((open) => !open);
            track("investor navigation toggled", { open: !menuOpen });
          }}
        >
          <CaretDown size={18} />
        </button>
      </header>

      <nav className="quick-nav" aria-label="Quick read shortcuts">
        <span>Quick read</span>
        <div>
          {quickLinks.map((link, index) => (
            <a
              href={`#${link.id}`}
              key={link.id}
              onClick={() => trackShortcut(link.id, "quick_nav")}
            >
              <em>{String(index + 1).padStart(2, "0")}</em>
              {link.label}
            </a>
          ))}
        </div>
      </nav>

      <main id="top">
        <section className="hero" data-track-section="hero">
          <div className="hero-copy">
            <h1>Building the assurance layer between code and production.</h1>
            <p className="hero-summary">
              A clear monthly record of product progress, traction, risks, and
              priorities for current and prospective investors.
            </p>
            <div className="hero-actions">
              <button type="button" onClick={() => selectMonth(latest, "hero")}>
                Read {latestMonthName} update
                <ArrowUpRight size={17} weight="bold" />
              </button>
              <a
                href="https://www.autter.dev/"
                target="_blank"
                rel="noreferrer"
                onClick={() => trackOutbound("autter_website", "hero")}
              >
                Visit Autter
              </a>
            </div>
          </div>

          <div className="hero-visual" aria-label="Autter product preview">
            <div className="visual-meta">
              <span>Latest product surface</span>
              <strong>Merge assurance</strong>
            </div>
            <img
              className="hero-platform"
              src="/assets/platform-screenshot-dark.png"
              alt="Autter platform showing a code assurance workflow"
            />
          </div>
        </section>

        <section className="snapshot" aria-label="Company snapshot">
          <div>
            <span>Stage</span>
            <strong>{companyThesis.stage}</strong>
          </div>
          <div>
            <span>Team</span>
            <strong>{companyThesis.team}</strong>
          </div>
          <div>
            <span>Current focus</span>
            <strong>{companyThesis.currentFocus}</strong>
          </div>
          <div>
            <span>Reporting period</span>
            <strong>March to {latestMonthName} 2026</strong>
          </div>
        </section>

        <section
          className="thesis-section"
          id="company"
          data-track-section="company_overview"
        >
          <div className="thesis-number">01</div>
          <div className="thesis-copy">
            <h2>Assurance for every software change.</h2>
            <p>{companyThesis.description}</p>
            <p>{companyThesis.product}</p>
          </div>
          <div className="thesis-proof">
            <ShieldCheck size={34} weight="duotone" />
            <strong>Why now</strong>
            <span>{companyThesis.whyNow}</span>
            <span>{companyThesis.durableOpportunity}</span>
          </div>
        </section>

        <section
          className="traction-section"
          id="traction"
          data-track-section="traction"
        >
          <div className="section-heading">
            <h2>Traction, with the caveats visible.</h2>
            <p>
              Reported figures are preserved as published. Missing values stay
              missing, and infrastructure activity is not presented as customer
              adoption.
            </p>
          </div>

          <div className="metric-strip">
            {(["newSignups", "activeUsers", "scanRuns", "mrr"] as MetricKey[]).map((key) => (
              <article key={key}>
                <span>{latestMonthName} {metricLabels[key].toLowerCase()}</span>
                <strong>
                  <MetricValue
                    value={latest.metrics[key]}
                    prefix={key === "mrr" ? "$" : ""}
                    compact={false}
                    maximumFractionDigits={key === "mrr" ? 2 : 0}
                  />
                </strong>
                <small>{growthNote(key)}</small>
              </article>
            ))}
          </div>

          <div className="chart-grid">
            <article className="chart-panel chart-wide">
              <div>
                <h3>Visitor activity and sign-ins</h3>
                <p>Monthly unique visitors and product sign-in events; missing months remain unreported</p>
              </div>
              <div className="chart-frame">
                <OverviewChart />
              </div>
            </article>
            <article className="chart-panel">
              <div>
                <h3>Infrastructure activity</h3>
                <p>API requests, reported only where available</p>
              </div>
              <div className="chart-frame">
                <ApiChart />
              </div>
            </article>
            <article className="chart-panel">
              <div>
                <h3>Reliability</h3>
                <p>Platform error rate by reported month</p>
              </div>
              <div className="chart-frame">
                <ReliabilityChart />
              </div>
            </article>
          </div>
        </section>

        <section
          className="workflow-section"
          aria-labelledby="workflow-title"
          data-track-section="workflow"
        >
          <div className="section-heading">
            <h2 id="workflow-title">
              How Autter turns repository context into an enforceable decision.
            </h2>
            <p>
              Autter builds an evidence chain around each change, then follows that
              change beyond the pull request.
            </p>
          </div>
          <ol className="workflow-list">
            {assuranceWorkflow.map((step, index) => (
              <li key={step.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="product-section"
          id="product"
          data-track-section="product_progress"
        >
          <div className="product-intro">
            <h2>The product moved from scan to enforcement.</h2>
            <p>
              Five months of work turned a manual codebase scan into a GitHub-native
              merge gate with application context, review evidence, and an audit
              trail, now used by paying teams.
            </p>
            <a
              href="https://handbook.autter.dev/"
              target="_blank"
              rel="noreferrer"
              onClick={() => trackOutbound("product_handbook", "product_section")}
            >
              Read the product handbook
              <ArrowUpRight size={16} />
            </a>
          </div>
          <div className="product-timeline">
            {monthlyUpdates.map((update, index) => (
              <button
                type="button"
                key={update.id}
                className={selectedId === update.id ? "timeline-row active" : "timeline-row"}
                onClick={() => selectMonth(update, "product_timeline")}
              >
                <span>{update.shortMonth}</span>
                <strong>{update.product[0]}</strong>
                <small>{update.product.slice(1).join(" / ")}</small>
                <em>{String(index + 1).padStart(2, "0")}</em>
              </button>
            ))}
          </div>
        </section>

        <section
          className="conviction-section"
          id="defensibility"
          data-track-section="defensibility"
        >
          <div className="defensibility-block">
            <h2>Why context and evidence can compound into defensibility.</h2>
            <div className="defensibility-list">
              {defensibility.map((item, index) => (
                <article key={item.title}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <aside className="proof-block">
            <h2>The questions that matter at this stage.</h2>
            <ol>
              {proofPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ol>
          </aside>
        </section>

        <section
          className="team-section"
          id="team"
          aria-labelledby="team-title"
          data-track-section="team"
        >
          <div className="team-intro">
            <h2 id="team-title">A two-person founding team.</h2>
            <p>
              Autter is intentionally capital-efficient while validating product
              usage and the commercial motion.
            </p>
          </div>
          <div className="founder-list">
            {founders.map((founder, index) => (
              <article key={founder.name}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{founder.name}</h3>
                <strong>{founder.role}</strong>
                <p>{founder.bio}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          className="monthly-section"
          id="monthly-report"
          data-track-section="monthly_updates"
        >
          <div className="monthly-heading">
            <div>
              <h2>Read every update.</h2>
            </div>
            <MonthRail
              selected={selectedId}
              onSelect={(update) => selectMonth(update, "monthly_rail")}
            />
          </div>

          <article className="monthly-report">
            <header className="report-header">
              <div>
                <span>{selected.month}</span>
                <h2>{selected.headline}</h2>
                <p>{selected.summary}</p>
              </div>
              <div className="report-status">
                <span>{selected.status}</span>
                <strong>Monthly update</strong>
              </div>
            </header>

            <div className="report-focus">
              <span>Primary focus</span>
              <strong>{selected.focus}</strong>
            </div>

            <div className="report-metrics">
              {(Object.keys(metricLabels) as MetricKey[]).map((key) => {
                if (!(key in selected.metrics)) return null;
                return (
                  <div key={key}>
                    <span>{metricLabels[key]}</span>
                    <strong>
                      <MetricValue
                        value={selected.metrics[key]}
                        prefix={key === "revenue" || key === "mrr" ? "$" : ""}
                        suffix={key === "errorRate" ? "%" : ""}
                        compact={false}
                        maximumFractionDigits={
                          key === "errorRate" || key === "revenue" || key === "mrr" ? 2 : 0
                        }
                      />
                    </strong>
                  </div>
                );
              })}
            </div>

            <p className="measurement-note">{selected.measurementNote}</p>

            <div className="report-lists">
              <DetailList title="What moved forward" items={selected.highlights} />
              <DetailList title="What did not work" items={selected.lows} tone="quiet" />
              <DetailList title="Where help matters" items={selected.asks} tone="accent" />
              <DetailList title="Next month" items={selected.next} />
            </div>
          </article>
        </section>

        <section
          className="current-asks-section"
          id="current-asks"
          aria-labelledby="asks-title"
          data-track-section="current_asks"
        >
          <div className="asks-heading">
            <h2 id="asks-title">Where the right introduction can help.</h2>
            <p>
              The pre-seed round is active. These are the highest-leverage ways to
              help Autter right now.
            </p>
          </div>
          <div className="current-asks-list">
            {currentAsks.map((ask, index) => (
              <article key={ask.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{ask.title}</h3>
                  <p>{ask.body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="ask-section" data-track-section="contact">
          <div>
            <EnvelopeSimple size={34} weight="duotone" />
            <h2>Interested in where Autter is going?</h2>
            <p>
              We welcome conversations with investors and engineering teams who
              understand developer tools, security, and AI infrastructure.
            </p>
          </div>
          <a
            href="mailto:sagnik@autter.dev"
            onClick={() => track("investor contact clicked", { location: "closing_cta" })}
          >
            Contact the team
            <ArrowUpRight size={17} weight="bold" />
          </a>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-primary">
          <a
            className="footer-brand"
            href="#top"
            aria-label="Back to the top"
            onClick={() => trackShortcut("top", "footer")}
          >
            <img src="/assets/autter-logo-dark.webp" alt="Autter" />
          </a>
          <p>
            AI assurance infrastructure
            <br />
            for software teams.
          </p>
        </div>
        <div className="footer-nav">
          <a href="#company" onClick={() => trackShortcut("company", "footer")}>
            Overview
          </a>
          <a
            href="#monthly-report"
            onClick={() => trackShortcut("monthly-report", "footer")}
          >
            Updates
          </a>
          <a
            href="#current-asks"
            onClick={() => trackShortcut("current-asks", "footer")}
          >
            Current asks
          </a>
        </div>
        <div className="footer-links">
          <a
            href="https://github.com/Autter-dev"
            target="_blank"
            rel="noreferrer"
            onClick={() => trackOutbound("github", "footer")}
          >
            <GithubLogo size={18} />
            GitHub
          </a>
          <a
            href="https://www.autter.dev/privacy"
            onClick={() => trackOutbound("privacy", "footer")}
          >
            Privacy
          </a>
          <a
            href="https://www.autter.dev/terms-of-service"
            onClick={() => trackOutbound("terms", "footer")}
          >
            Terms
          </a>
          <a
            href="mailto:sagnik@autter.dev"
            onClick={() => track("investor contact clicked", { location: "footer" })}
          >
            sagnik@autter.dev
          </a>
        </div>
        <small>© {new Date().getFullYear()} Autter. Monthly investor updates.</small>
      </footer>
    </div>
  );
}

export default App;
